/**
 * Reusable Aura Character Component
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const AuraCharacter = ({ 
  context = 'game',
  initialMessage = '',
  onMessageChange,
  compact = false,
  position = 'bottom-right'
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [messages, setMessages] = useState([]);
  const [isThinking, setIsThinking] = useState(false);

  useEffect(() => {
    if (initialMessage) {
      setMessages([{
        id: Date.now(),
        text: initialMessage,
        type: 'aura',
        timestamp: new Date()
      }]);
    }
  }, [initialMessage]);

  const addMessage = (text, type = 'aura') => {
    const newMessage = {
      id: Date.now(),
      text,
      type,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
    if (onMessageChange) {
      onMessageChange(text);
    }
    setTimeout(() => {
      const container = document.getElementById('aura-messages-container');
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 100);
  };

  const handleUserMessage = async (userInput) => {
    if (!userInput.trim()) return;

    const userMsg = {
      id: Date.now(),
      text: userInput,
      type: 'user',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);
    setIsThinking(true);

    try {
      const response = await axios.post('/api/ai-assistant/chat', {
        message: userInput,
        context: context
      });

      if (response.data && response.data.response) {
        addMessage(response.data.response, 'aura');
      } else {
        addMessage("I'm here to help, but I'm still learning. How can I assist you?", 'aura');
      }
    } catch (error) {
      console.error('Error getting Aura response:', error);
      addMessage("Sorry, I'm having trouble responding right now. Please try again.", 'aura');
    } finally {
      setIsThinking(false);
    }
  };

  const positionClasses = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4'
  };

  const positionClass = positionClasses[position] || positionClasses['bottom-right'];

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`fixed ${positionClass} z-50`}
      >
        <motion.button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-14 h-14 btn-primary rounded-full"
        >
          <SparklesIcon className="w-6 h-6" />
        </motion.button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className={`fixed ${positionClass} z-50 w-80 max-w-[calc(100vw-2rem)]`}
    >
      <motion.div
        className="surface-emphasis surface-dark overflow-hidden rounded-lg border border-hairline"
        layout
      >
        <div className="p-4 flex items-center justify-between border-b border-hairline">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-pine rounded-full flex items-center justify-center">
              <SparklesIcon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-sm">Aura</h3>
              <p className="text-secondary text-xs">Your Financial Guide</p>
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="icon-btn"
          >
            {isExpanded ? <ChevronDownIcon className="w-5 h-5" /> : <ChevronUpIcon className="w-5 h-5" />}
          </button>
        </div>

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div
                id="aura-messages-container"
                className="h-64 overflow-y-auto p-4 space-y-3 bg-pine-deep"
              >
                {messages.length === 0 ? (
                  <div className="text-secondary text-sm text-center py-8">
                    <p>Hi! I'm Aura, your financial guide.</p>
                    <p className="mt-2 text-xs text-faint">I'm here to help you learn and make better financial decisions.</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${
                          msg.type === 'user'
                            ? 'bg-pine light-surface'
                            : 'light-surface surface-panel'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                        <p className="text-xs text-faint mt-1">
                          {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </motion.div>
                  ))
                )}
                {isThinking && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className="light-surface surface-panel rounded-lg p-3">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-pine rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 bg-pine rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 bg-pine rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {context === 'chat' && (
                <div className="p-3 border-t border-hairline">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      placeholder="Ask Aura..."
                      className="input-editorial flex-1 text-sm py-2"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleUserMessage(e.target.value);
                          e.target.value = '';
                        }
                      }}
                    />
                    <button
                      onClick={(e) => {
                        const input = e.target.previousElementSibling;
                        if (input) {
                          handleUserMessage(input.value);
                          input.value = '';
                        }
                      }}
                      className="btn-primary px-4 py-2 text-sm"
                    >
                      Send
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default AuraCharacter;
